(() => {
  if (document.getElementById("ai-bot-widget")) return;

  // =========================
  // CONFIG
  // =========================
  const STORYLINE_CONFIG = window.STORYLINE_AI_CONFIG || {};

  const BACKEND_URL = STORYLINE_CONFIG.backendUrl || "";
  const HAS_BACKEND = typeof BACKEND_URL === "string" && BACKEND_URL.trim() !== "";

  const BOT_LABELS = STORYLINE_CONFIG.botLabels || {
    shopper_momma: "Shopper Momma Assistant",
    d_esports: "D-AR Esports Assistant",
    gcr: "GCR Creator Assistant"
  };

  const COLLECTION_URLS = STORYLINE_CONFIG.collectionUrls || {
    shopper_momma: "/collections/shopper-momma-shop",
    d_esports: "/collections/dredlen",
    gcr: "/collections/gcr-i-o"
  };

  const NFL_DRAFT_START_DATE =
    STORYLINE_CONFIG.nflDraftStartDate || "2026-04-23T00:00:00";

  const SHOPPER_COUNTDOWN_LABEL =
    STORYLINE_CONFIG.shopperMommaCountdownLabel ||
    "DETERMINE THE STRENGTH BY APRIL 23, 2026";

  const POPULAR_ENDPOINT =
    STORYLINE_CONFIG.popularEndpoint || "/popular";

  const INFO_LINKS = {
    printfulPricing: "https://www.printful.com/pricing",
    yoycolHome: "https://www.yoycol.com/",
    refundPolicy: "https://www.storyline-store.com/policies/refund-policy",
    privacyPolicy: "https://www.storyline-store.com/policies/privacy-policy",
    termsOfService: "https://www.storyline-store.com/policies/terms-of-service"
  };

  const STORE_INFO = {
    pricingSummary:
      "We use print-on-demand fulfillment partners and compare product cost, shipping, and production fit across providers before setting retail prices.",
    refundSummary:
      "Our current refund policy lasts 15 days from purchase. Items must be unused, in original packaging, and accompanied by proof of purchase.",
    privacySummary:
      "Our privacy policy explains what customer information is collected during browsing and checkout, how it is used, and how customers can contact us about their data.",
    termsSummary:
      "Our terms explain store usage rules, product availability, price changes, order limitations, and service disclaimers.",
    printfulSummary:
      "Printful has a free entry option and paid growth options. Product costs generally depend on the base item, decoration method, fulfillment, shipping, and any extras.",
    yoycolSummary:
      "Yoycol is a print-on-demand and dropshipping provider with no minimum order requirement. Costs vary by product, fulfillment, and shipping."
  };

  const STORYLINE = {
    greeting:
      "Welcome to Storyline Store AI. Ask about Shopper Momma drops, DREDLEN esports gear, GCR graphics, faction styles, store policies, pricing, or the NFL Draft countdown.",
    shopperMommaSummary:
      "Shopper Momma is real-time culture commentary apparel. Each drop reacts to media, sports, and social moments.",
    dredlenSummary:
      "DREDLEN AR Esports connects gaming culture with physical apparel and future AR game experiences.",
    gcrSummary:
      "GCR blends underground 90s graphic illustration energy with cinematic modern rendering, dark satire, rebellious streetwear attitude, and anti-minimalist design.",
    storylineSummary:
      "Storyline turns retail into a story engine. Purchases are votes, and participation helps shape the world.",
    factionSummary:
      "Season 01 has four factions: Aggression, Obsession, Seduction, and Attention. Each faction reflects a style and product energy.",
    nflDraftSummary:
      "The 2026 NFL Draft runs April 23 to April 25, 2026. Shopper Momma countdown drops can tie directly into draft culture and reaction-driven releases."
  };

  const FACTIONS = {
    aggression: {
      label: "Aggression",
      description: "Power, dominance, confrontation, armor silhouettes, heavy graphics, weapon motifs.",
      keywords: ["aggressive", "power", "dominant", "armor", "heavy", "skull", "blade", "intense", "battle", "force"]
    },
    obsession: {
      label: "Obsession",
      description: "Precision, repetition, collectibles, serialized items, badges, completionist energy.",
      keywords: ["collectible", "serial", "precise", "ritual", "score", "badge", "limited set", "completionist", "repeat", "precision"]
    },
    seduction: {
      label: "Seduction",
      description: "Desire, allure, rarity, color variants, secret unlocks, seen-once items.",
      keywords: ["rare", "allure", "secret", "variant", "exclusive", "seen once", "desire", "seductive", "mystery"]
    },
    attention: {
      label: "Attention",
      description: "Visibility, spectacle, loud graphics, hats, jerseys, viral statement pieces.",
      keywords: ["loud", "viral", "hat", "jersey", "statement", "spectacle", "bright", "public", "attention", "showy"]
    }
  };

  const PRODUCT_RULES = {
    archivedTags: ["archived", "legacy", "discontinued", "out-of-manufacturing"],
    hiddenTags: ["hidden-from-bot", "internal-only"],
    soldOutMessage: "This item is currently sold out.",
    archivedMessage: "This item is no longer in production.",
    fallbackPriceMessage: "See product page for current pricing."
  };

  // =========================
  // STORAGE / STATE
  // =========================
  function loadJSON(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (err) {
      return fallback;
    }
  }

  function saveJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (err) {
      // ignore storage failures
    }
  }

  const state = {
    purchaseVotes: loadJSON("storyline_purchase_votes", {
      aggression: 0,
      obsession: 0,
      seduction: 0,
      attention: 0
    }),
    secretUnlocks: loadJSON("storyline_secret_unlocks", {}),
    nfcScans: loadJSON("storyline_nfc_scans", []),
    storyState: loadJSON("storyline_story_state", {
      dominantFaction: "attention",
      totalVotes: 0
    })
  };

  function updateStoryState() {
    const votes = state.purchaseVotes;
    const sorted = Object.entries(votes).sort((a, b) => b[1] - a[1]);
    state.storyState.dominantFaction = sorted[0] ? sorted[0][0] : "attention";
    state.storyState.totalVotes = Object.values(votes).reduce((sum, v) => sum + v, 0);
    saveJSON("storyline_story_state", state.storyState);
  }

  function voteForFaction(factionKey) {
    if (!Object.prototype.hasOwnProperty.call(state.purchaseVotes, factionKey)) {
      state.purchaseVotes[factionKey] = 0;
    }
    state.purchaseVotes[factionKey] += 1;
    saveJSON("storyline_purchase_votes", state.purchaseVotes);
    updateStoryState();
  }

  function unlockSecret(key) {
    state.secretUnlocks[key] = true;
    saveJSON("storyline_secret_unlocks", state.secretUnlocks);
  }

  function recordNfcScan(payload) {
    state.nfcScans.push({
      ...payload,
      scannedAt: new Date().toISOString()
    });
    saveJSON("storyline_nfc_scans", state.nfcScans);
  }

  // =========================
  // ROUTE / BOT DETECTION
  // =========================
  function detectBot() {
    const path = (window.location.pathname || "").toLowerCase();

    if (
      path.includes("/collections/dredlen") ||
      path.includes("/collections/d-") ||
      path.includes("/products/d-")
    ) {
      return "d_esports";
    }

    if (
      path.includes("/collections/gcr") ||
      path.includes("/collections/gcr-i-o") ||
      path.includes("/products/gcr")
    ) {
      return "gcr";
    }

    return "shopper_momma";
  }

  function getCollectionUrl(bot = detectBot()) {
    return COLLECTION_URLS[bot] || "/collections/all";
  }

  function getShopUrl(bot = detectBot()) {
    return COLLECTION_URLS[bot] || "/collections/all";
  }

  // =========================
  // HELPERS
  // =========================
  function escapeHtml(str) {
    return String(str || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;");
  }

  function normalizeText(str) {
    return String(str || "")
      .toLowerCase()
      .replace(/[’']/g, "'")
      .replace(/\s+/g, " ")
      .trim();
  }

  function daysUntil(dateString) {
    const now = new Date();
    const target = new Date(dateString);
    const diff = target.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  function normalizeProductUrl(url) {
    if (!url) return "#";
    if (url.startsWith("http")) return url;
    return url.startsWith("/") ? url : `/${url}`;
  }

  function getProductStatus(product) {
    const tags = Array.isArray(product.tags)
      ? product.tags.map((t) => String(t).toLowerCase())
      : String(product.tags || "")
          .split(",")
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean);

    const archived = tags.some((tag) => PRODUCT_RULES.archivedTags.includes(tag));
    const hidden = tags.some((tag) => PRODUCT_RULES.hiddenTags.includes(tag));
    const soldOut =
      product.available === false ||
      product.availableForSale === false ||
      product.in_stock === false;

    return {
      archived,
      hidden,
      soldOut,
      available: !archived && !hidden && !soldOut
    };
  }

  function getDisplayPrice(product) {
    const status = getProductStatus(product);

    if (status.archived) return "";
    if (status.soldOut) return "Sold out";

    if (typeof product.price === "number") {
      return `$${(product.price / 100).toFixed(2)}`;
    }

    if (typeof product.price === "string" && product.price.trim()) {
      return escapeHtml(product.price);
    }

    return "";
  }

  function formatProductForBot(product) {
    const status = getProductStatus(product);
    if (status.hidden) return null;

    let why = product.why || "";
    if (status.archived) {
      why = PRODUCT_RULES.archivedMessage;
    } else if (status.soldOut) {
      why = PRODUCT_RULES.soldOutMessage;
    }

    return {
      ...product,
      why,
      price: getDisplayPrice(product)
    };
  }

  function prepareProductsForBot(products) {
    return (products || [])
      .map(formatProductForBot)
      .filter(Boolean);
  }

  // =========================
  // UI
  // =========================
  const widget = document.createElement("div");
  widget.id = "ai-bot-widget";
  widget.innerHTML = `
    <div id="ai-bot-launcher">AI</div>

    <div id="ai-bot-panel" style="display:none;">
      <div id="ai-bot-header">
        <span id="ai-bot-title">Storyline Store AI</span>
        <button id="ai-bot-close" aria-label="Close chat" type="button">&times;</button>
      </div>

      <div id="ai-bot-messages"></div>

<div id="ai-bot-cta-row">
  <button class="ai-quick-btn" data-ai-action="shop">Shop Now</button>
  <button class="ai-quick-btn" data-ai-action="collection">View Collection</button>
  <button class="ai-quick-btn" data-ai-action="newest">Newest Drops</button>
  <button class="ai-quick-btn" data-ai-action="trending">Trending</button>
  <button class="ai-quick-btn" data-ai-action="faction">What Faction Are You?</button>
  <button class="ai-quick-btn" data-ai-action="draft">Draft Countdown</button>
  <button class="ai-quick-btn" data-ai-action="story">How The Story Works</button>
  <button class="ai-quick-btn" data-ai-action="refund">Refund Policy</button>
  <button class="ai-quick-btn" data-ai-action="privacy">Privacy Policy</button>
  <button class="ai-quick-btn" data-ai-action="terms">Terms of Service</button>
</div>

      <div id="ai-bot-input-row">
        <input id="ai-bot-input" placeholder="Ask about products, policy, or the story..." />
        <button id="ai-bot-send" type="button">Send</button>
      </div>
    </div>
  `;

  const style = document.createElement("style");
  style.textContent = `
    #ai-bot-widget {
      position: fixed;
      right: 20px;
      bottom: 20px;
      z-index: 999999;
      font-family: Arial, sans-serif;
    }

    #ai-bot-launcher {
      background: linear-gradient(135deg, #7c3aed, #5b21b6);
      color: #fff;
      padding: 14px 18px;
      border-radius: 999px;
      cursor: pointer;
      font-weight: 700;
      box-shadow: 0 12px 35px rgba(0,0,0,0.28);
      user-select: none;
      min-width: 68px;
      text-align: center;
      letter-spacing: 0.02em;
    }

    #ai-bot-panel {
      width: 390px;
      height: 600px;
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 18px 55px rgba(0,0,0,0.30);
      overflow: hidden;
      margin-top: 12px;
      display: flex;
      flex-direction: column;
      border: 1px solid rgba(0,0,0,0.08);
    }

    #ai-bot-header {
      padding: 14px 16px;
      background: linear-gradient(135deg, #7c3aed, #5b21b6);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-weight: 700;
      font-size: 16px;
    }

    #ai-bot-close {
      border: none;
      background: transparent;
      color: #fff;
      font-size: 22px;
      cursor: pointer;
      line-height: 1;
    }

    #ai-bot-messages {
      flex: 1;
      overflow-y: auto;
      padding: 14px;
      font-size: 14px;
      background: #f8fafc;
    }

    .ai-msg {
      margin: 10px 0;
      line-height: 1.45;
    }

    .ai-bubble {
      display: inline-block;
      max-width: 92%;
      padding: 10px 12px;
      border-radius: 14px;
      word-break: break-word;
    }

    .ai-user .ai-bubble {
      background: #111827;
      color: #fff;
      border-bottom-right-radius: 4px;
    }

    .ai-bot .ai-bubble {
      background: #fff;
      color: #111827;
      border: 1px solid #e5e7eb;
      border-bottom-left-radius: 4px;
    }

    .ai-label {
      font-weight: 700;
      margin-bottom: 4px;
      display: block;
      font-size: 12px;
      opacity: 0.75;
    }

    #ai-bot-cta-row {
      display: flex;
      gap: 8px;
      padding: 10px 12px;
      border-top: 1px solid #e5e7eb;
      border-bottom: 1px solid #e5e7eb;
      background: #fff;
      overflow-x: auto;
    }

    .ai-quick-btn {
      border: none;
      padding: 8px 10px;
      border-radius: 999px;
      background: #ede9fe;
      color: #4c1d95;
      cursor: pointer;
      font-size: 12px;
      font-weight: 700;
      white-space: nowrap;
    }

    #ai-bot-input-row {
      display: flex;
      border-top: 1px solid #e5e7eb;
      background: #fff;
    }

    #ai-bot-input {
      flex: 1;
      border: none;
      padding: 14px;
      outline: none;
      font-size: 14px;
    }

    #ai-bot-send {
      border: none;
      padding: 0 16px;
      cursor: pointer;
      background: #111827;
      color: #fff;
      font-weight: 700;
    }

    .ai-carousel {
      display: flex;
      gap: 10px;
      overflow-x: auto;
      padding: 8px 0 2px;
      margin-top: 8px;
    }

    .ai-card {
      min-width: 180px;
      max-width: 180px;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 10px;
      background: #fff;
      box-shadow: 0 4px 12px rgba(0,0,0,0.06);
    }

    .ai-card img {
      width: 100%;
      height: 150px;
      object-fit: cover;
      border-radius: 8px;
      display: block;
      background: #f3f4f6;
    }

    .ai-card-title {
      font-weight: 700;
      margin-top: 8px;
      font-size: 13px;
      color: #111827;
    }

    .ai-card-price {
      margin-top: 4px;
      font-size: 13px;
      color: #374151;
      opacity: 0.95;
    }

    .ai-card-why {
      margin-top: 6px;
      font-size: 12px;
      color: #6b7280;
    }

    .ai-buy {
      margin-top: 10px;
      width: 100%;
      border: none;
      padding: 9px 10px;
      cursor: pointer;
      border-radius: 8px;
      background: #6d28d9;
      color: #fff;
      font-weight: 700;
    }

    .ai-map,
    .ai-faction-panel,
    .ai-info-panel {
      margin-top: 10px;
      padding: 12px;
      border-radius: 12px;
      background: #111827;
      color: #fff;
    }

    .ai-progress {
      width: 100%;
      height: 8px;
      border-radius: 999px;
      background: #e5e7eb;
      overflow: hidden;
      margin-top: 6px;
    }

    .ai-progress-fill {
      height: 100%;
      background: #6d28d9;
    }

    .ai-bot a {
      color: #4c1d95;
      text-decoration: underline;
    }
  `;

  document.head.appendChild(style);
  document.body.appendChild(widget);

  const launcher = document.getElementById("ai-bot-launcher");
  const panel = document.getElementById("ai-bot-panel");
  const closeBtn = document.getElementById("ai-bot-close");
  const titleEl = document.getElementById("ai-bot-title");
  const messages = document.getElementById("ai-bot-messages");
  const input = document.getElementById("ai-bot-input");
  const sendBtn = document.getElementById("ai-bot-send");
  const ctaRow = document.getElementById("ai-bot-cta-row");

  function addMessage(html, type = "bot") {
    if (!messages) return;
    const div = document.createElement("div");
    div.className = `ai-msg ai-${type}`;
    div.innerHTML = `<div class="ai-bubble">${html}</div>`;
    messages.appendChild(div);
    messages.scrollTop = messages.scrollHeight;
  }

  function addUserMessage(text) {
    addMessage(`<span class="ai-label">You</span>${escapeHtml(text)}`, "user");
  }

  function addBotMessage(html) {
    const botName = titleEl ? titleEl.textContent || "AI" : "AI";
    addMessage(`<span class="ai-label">${escapeHtml(botName)}</span>${html}`, "bot");
  }

  function renderCarousel(products) {
    const safeProducts = prepareProductsForBot(products);
    if (!Array.isArray(safeProducts) || safeProducts.length === 0) return "";

    const cards = safeProducts.map((p) => {
      const image = escapeHtml(
        (p.featured_image && p.featured_image.url) ||
        p.image ||
        p.image_url ||
        ""
      );
      const title = escapeHtml(p.title || "Product");
      const url = escapeHtml(normalizeProductUrl(p.url || "#"));
      const why = escapeHtml(p.why || "");
      const price = typeof p.price === "string" ? p.price : getDisplayPrice(p);

      return `
        <div class="ai-card">
          <img src="${image}" alt="${title}" loading="lazy">
          <div class="ai-card-title">${title}</div>
          ${price ? `<div class="ai-card-price">${price}</div>` : ""}
          ${why ? `<div class="ai-card-why">${why}</div>` : ""}
          <button class="ai-buy" onclick="window.open('${url}', '_blank', 'noopener,noreferrer')">View</button>
        </div>
      `;
    }).join("");

    return `<div class="ai-carousel">${cards}</div>`;
  }

  function renderWorldMap() {
    updateStoryState();
    const votes = state.purchaseVotes;
    const total = Math.max(1, state.storyState.totalVotes);

    function row(key) {
      const percent = Math.round((votes[key] / total) * 100);
      return `
        <div style="margin-top:10px;">
          <div style="display:flex;justify-content:space-between;">
            <strong>${FACTIONS[key].label}</strong><span>${votes[key]} votes</span>
          </div>
          <div class="ai-progress"><div class="ai-progress-fill" style="width:${percent}%;"></div></div>
        </div>
      `;
    }

    return `
      <div class="ai-map">
        <strong>Live Storyline World Map</strong><br>
        Dominant faction: <strong>${escapeHtml(FACTIONS[state.storyState.dominantFaction].label)}</strong><br>
        Total votes: ${state.storyState.totalVotes}
        ${row("aggression")}
        ${row("obsession")}
        ${row("seduction")}
        ${row("attention")}
      </div>
    `;
  }

  function guessFactionFromText(text) {
    const q = normalizeText(text);
    for (const [key, info] of Object.entries(FACTIONS)) {
      if (info.keywords.some((kw) => q.includes(kw))) return key;
    }
    return null;
  }

  function maybeShowFactionResult(question) {
    const factionKey = guessFactionFromText(question);
    if (!factionKey) return false;

    addBotMessage(`
      <div class="ai-faction-panel">
        <strong>You may align with ${escapeHtml(FACTIONS[factionKey].label)}</strong><br>
        ${escapeHtml(FACTIONS[factionKey].description)}
      </div>
    `);
    return true;
  }

  function maybeHandleNfcUrl() {
    const params = new URLSearchParams(window.location.search);
    const tag = params.get("nfc");
    if (!tag) return;

    recordNfcScan({ tag, path: window.location.pathname });
    unlockSecret(`nfc:${tag}`);

    addBotMessage(`
      Secret scan detected.<br>
      NFC tag: <strong>${escapeHtml(tag)}</strong><br>
      A digital unlock has been recorded for this session.
    `);
  }

  // =========================
  // PRODUCT LOOKUPS
  // =========================
  async function fetchShopifySuggestions(query, limit = 6) {
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${limit}`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" }
    });

    if (!res.ok) throw new Error("Product lookup failed");

    const data = await res.json();
    const products =
      (data.resources && data.resources.results && data.resources.results.products) || [];

    return products.map((p) => ({
      ...p,
      url: normalizeProductUrl(p.url),
      available: p.available !== false
    }));
  }

  async function fetchCollectionProducts(handle, sortBy = "created-descending") {
    const url = `/collections/${handle}/products.json?limit=12`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" }
    });

    if (!res.ok) throw new Error("Collection lookup failed");

    const data = await res.json();
    let products = Array.isArray(data.products) ? data.products : [];

    if (sortBy === "created-descending") {
      products = products.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    if (sortBy === "price-descending") {
      products = products.sort(
        (a, b) =>
          Number((b.variants && b.variants[0] && b.variants[0].price) || 0) -
          Number((a.variants && a.variants[0] && a.variants[0].price) || 0)
      );
    }

    return products.map((p) => ({
      title: p.title,
      url: `/products/${p.handle}`,
      price:
        p.variants && p.variants[0] && p.variants[0].price
          ? `$${Number(p.variants[0].price).toFixed(2)}`
          : "",
      image_url: (p.images && p.images[0] && p.images[0].src) || "",
      featured_image: {
        url: (p.images && p.images[0] && p.images[0].src) || ""
      },
      tags: p.tags || [],
      available: p.variants ? p.variants.some((v) => v.available) : true
    }));
  }

  async function getNewestDrops(bot) {
    const handle =
      bot === "d_esports" ? "dredlen" :
      bot === "gcr" ? "gcr-i-o" :
      "shopper-momma-shop";

    try {
      const items = await fetchCollectionProducts(handle, "created-descending");
      return items.slice(0, 4).map((p) => ({
        ...p,
        why: "Newest drop"
      }));
    } catch (err) {
      return [];
    }
  }

  async function getBestHoodies(bot) {
    const query =
      bot === "d_esports" ? "dredlen hoodie" :
      bot === "gcr" ? "gcr hoodie" :
      "hoodie";

    const results = await fetchShopifySuggestions(query, 8);
    return results.slice(0, 4).map((p) => ({
      ...p,
      why: "Hoodie pick"
    }));
  }

  async function getLimitedEditionItems(bot) {
    const terms =
      bot === "d_esports" ? "limited dredlen" :
      bot === "gcr" ? "limited gcr" :
      "limited shopper momma";

    const results = await fetchShopifySuggestions(terms, 8);
    return results.slice(0, 4).map((p) => ({
      ...p,
      why: "Limited / special drop"
    }));
  }

  async function getGamingApparel() {
    const results = await fetchShopifySuggestions("dredlen esports gaming", 8);
    return results.slice(0, 4).map((p) => ({
      ...p,
      why: "Gaming-themed apparel"
    }));
  }

  async function getPopularItemsThisWeek(bot) {
    if (HAS_BACKEND) {
      try {
        const res = await fetch(`${BACKEND_URL}?mode=popular&bot=${encodeURIComponent(bot)}`);
        if (res.ok) {
          const data = await res.json();
          return data.products || [];
        }
      } catch (err) {
        // fall through
      }
    }

    const query =
      bot === "d_esports" ? "dredlen" :
      bot === "gcr" ? "gcr" :
      "shopper momma";

    const results = await fetchShopifySuggestions(query, 8);
    return results.slice(0, 4).map((p) => ({
      ...p,
      why: "Popular now"
    }));
  }

  // =========================
  // ANSWERS
  // =========================
  function getStorePolicyAnswer(question) {
  const q = normalizeText(question);

  if (
    q.includes("refund policy") ||
    q.includes("can i return") ||
    q.includes("return an item") ||
    q.includes("returns")
  ) {
    return `
      Returns are handled according to our store refund policy.
      <br><br>
      <a href="/policies/refund-policy" target="_blank" rel="noopener noreferrer">View Refund Policy</a>
    `;
  }

  if (
    q.includes("privacy policy") ||
    q.includes("your privacy policy") ||
    q.includes("how do you handle my data") ||
    q.includes("data policy")
  ) {
    return `
      Our privacy policy explains how customer information is collected, used, and protected.
      <br><br>
      <a href="/policies/privacy-policy" target="_blank" rel="noopener noreferrer">View Privacy Policy</a>
    `;
  }

  if (
    q.includes("terms of service") ||
    q.includes("terms") ||
    q.includes("store terms")
  ) {
    return `
      Our terms of service explain the rules, conditions, and policies for using the store and placing orders.
      <br><br>
      <a href="/policies/terms-of-service" target="_blank" rel="noopener noreferrer">View Terms of Service</a>
    `;
  }

  if (
    q.includes("payment methods") ||
    q.includes("what payment methods do you accept") ||
    q.includes("how can i pay") ||
    q.includes("how do i pay")
  ) {
    return `
      Payment methods are shown at checkout and may include major credit cards, Shop Pay, and other Shopify-supported payment options depending on your region and device.
    `;
  }

  if (
    q.includes("do you have my size") ||
    q.includes("what sizes do you have") ||
    q.includes("size available") ||
    q.includes("what size") ||
    q.includes("size chart")
  ) {
    return `
      Size availability can vary by product. Please open the product page you want and check the size selector and sizing details there. If you send me the product name, I can help guide you to the right item.
    `;
  }

    if (
    q.includes("can i customize a hoodie") ||
    q.includes("customize a hoodie") ||
    q.includes("custom hoodie") ||
    q.includes("personalize a hoodie") ||
    q.includes("can i customize")
  ) {
    return `
      Customization depends on the specific product and collection. At the moment, not every item is customizable. If you have a specific hoodie or design in mind, send me the product name and I can point you in the best direction.
    `;
  }

  return "";
}

function getPolicyOrPricingAnswer(question) {
  const q = normalizeText(question);

  if (q.includes("refund policy")) {
    return {
      html:
        `You can review our refund policy here:<br><br>` +
        `<a href="/policies/refund-policy" target="_blank" rel="noopener noreferrer">View Refund Policy</a>`
    };
  }

  if (q.includes("privacy policy")) {
    return {
      html:
        `You can review our privacy policy here:<br><br>` +
        `<a href="/policies/privacy-policy" target="_blank" rel="noopener noreferrer">View Privacy Policy</a>`
    };
  }

  if (q.includes("terms of service") || q.includes("terms")) {
    return {
      html:
        `You can review our terms of service here:<br><br>` +
        `<a href="/policies/terms-of-service" target="_blank" rel="noopener noreferrer">View Terms of Service</a>`
    };
  }

  if (q.includes("printful")) {
    return {
      html:
        `${escapeHtml(STORE_INFO.printfulSummary)}<br><br>` +
        `<a href="${INFO_LINKS.printfulPricing}" target="_blank" rel="noopener noreferrer">View Printful Pricing</a>`
    };
  }

  if (q.includes("yoycol")) {
    return {
      html:
        `${escapeHtml(STORE_INFO.yoycolSummary)}<br><br>` +
        `<a href="${INFO_LINKS.yoycolHome}" target="_blank" rel="noopener noreferrer">View YOYCOL</a>`
    };
  }

  return null;
}

function getStorePolicyAnswer(question) {
  const q = normalizeText(question);

  if (
    q.includes("refund policy") ||
    q.includes("can i return") ||
    q.includes("return an item") ||
    q.includes("returns")
  ) {
    return `
      Returns are handled according to our store refund policy.
      <br><br>
      <a href="/policies/refund-policy" target="_blank" rel="noopener noreferrer">View Refund Policy</a>
    `;
  }

  if (
    q.includes("privacy policy") ||
    q.includes("your privacy policy") ||
    q.includes("how do you handle my data") ||
    q.includes("data policy")
  ) {
    return `
      Our privacy policy explains how customer information is collected, used, and protected.
      <br><br>
      <a href="/policies/privacy-policy" target="_blank" rel="noopener noreferrer">View Privacy Policy</a>
    `;
  }

  if (
    q.includes("terms of service") ||
    q.includes("terms") ||
    q.includes("store terms")
  ) {
    return `
      Our terms of service explain the rules, conditions, and policies for using the store and placing orders.
      <br><br>
      <a href="/policies/terms-of-service" target="_blank" rel="noopener noreferrer">View Terms of Service</a>
    `;
  }

  if (
    q.includes("payment methods") ||
    q.includes("what payment methods do you accept") ||
    q.includes("how can i pay") ||
    q.includes("how do i pay")
  ) {
    return `
      Payment methods are shown at checkout and may include major credit cards, Shop Pay, and other Shopify-supported payment options depending on your region and device.
    `;
  }

  if (
    q.includes("do you have my size") ||
    q.includes("what sizes do you have") ||
    q.includes("size available") ||
    q.includes("what size") ||
    q.includes("size chart")
  ) {
    return `
      Size availability can vary by product. Please open the product page you want and check the size selector and sizing details there. If you send me the product name, I can help guide you to the right item.
    `;
  }

  if (
    q.includes("can i customize a hoodie") ||
    q.includes("customize a hoodie") ||
    q.includes("custom hoodie") ||
    q.includes("personalize a hoodie") ||
    q.includes("can i customize")
  ) {
    return `
      Customization depends on the specific product and collection. At the moment, not every item is customizable. If you have a specific hoodie or design in mind, send me the product name and I can point you in the best direction.
    `;
  }

  return "";
}

async function handleTopQuestion(question) {
  const q = normalizeText(question);
  const bot = detectBot();

  if (
    q.includes("newest drops") ||
    q.includes("what s new") ||
    q.includes("whats new") ||
    q === "newest" ||
    q === "what s new" ||
    q === "what is new"
  ) {
    return {
      text: "Here are the newest drops:",
      products: await getNewestDrops(bot)
    };
  }

  if (
    q.includes("trending right now") ||
    q.includes("what s trending") ||
    q.includes("whats trending") ||
    q === "trending"
  ) {
    return {
      text: "Here is what is trending right now:",
      products: await getPopularItemsThisWeek(bot)
    };
  }

  if (
    q.includes("best hoodie") ||
    q.includes("hoodie right now")
  ) {
    return {
      text: "Here are some hoodie picks worth checking out:",
      products: await getBestHoodies(bot)
    };
  }

  if (
    q.includes("limited edition") ||
    q.includes("show me limited") ||
    q.includes("limited items")
  ) {
    return {
      text: "Here are some limited or special-drop style items:",
      products: await getLimitedEditionItems(bot)
    };
  }

  if (
    q.includes("gaming themed apparel") ||
    q.includes("gaming apparel") ||
    q.includes("esports gear")
  ) {
    return {
      text: "Here are some gaming-themed apparel picks:",
      products: await getGamingApparel()
    };
  }

  if (q.includes("what s new in the gcr collection") || q.includes("whats new in the gcr collection")) {
    return {
      text: `${STORYLINE.gcrSummary} Here is what looks newest in GCR:`,
      products: await getNewestDrops("gcr")
    };
  }

  if (q.includes("show me dredlen") || q.includes("dredlen esports gear")) {
    const products = await fetchShopifySuggestions("dredlen esports", 8);
    return {
      text: `${STORYLINE.dredlenSummary} Here are some DREDLEN gear picks:`,
      products: products.slice(0, 4).map((p) => ({
        ...p,
        why: "DREDLEN esports gear"
      }))
    };
  }

  if (q.includes("what is shopper momma") || q === "shopper momma") {
    const products = await fetchShopifySuggestions("shopper momma", 8);
    return {
      text: `${STORYLINE.shopperMommaSummary} Here are some Shopper Momma items to start with:`,
      products: products.slice(0, 4).map((p) => ({
        ...p,
        why: "Shopper Momma pick"
      }))
    };
  }

  return null;
}

  // =========================
  // MAIN LOGIC
  // =========================
  let draftCountdownEl = null;
  let draftCountdownTimerStarted = false;

  function renderDraftCountdown() {
    const days = daysUntil(NFL_DRAFT_START_DATE);
    const html = `NFL Draft countdown: <strong>${days} days</strong> until April 23, 2026.`;

    if (!draftCountdownTimerStarted) {
      draftCountdownTimerStarted = true;
      const msUntilMidnight = new Date().setHours(24, 0, 0, 0) - Date.now();
      setTimeout(() => {
        renderDraftCountdown();
        setInterval(renderDraftCountdown, 24 * 60 * 60 * 1000);
      }, msUntilMidnight);
    }

    if (draftCountdownEl) {
      draftCountdownEl.innerHTML = html;
      return;
    }

    addBotMessage(html);
    const bubbles = document.querySelectorAll("#ai-bot-messages .ai-bot .ai-bubble");
    if (bubbles.length) {
      draftCountdownEl = bubbles[bubbles.length - 1];
    }
  }

  async function tryLocalAnswer(question) {
    const topAnswer = await handleTopQuestion(question);
    if (topAnswer) {
      addBotMessage(
        `${escapeHtml(topAnswer.text)}${renderCarousel(topAnswer.products || [])}`
      );
      return true;
    }

    const storePolicyAnswer = getStorePolicyAnswer(question);
    if (storePolicyAnswer) {
      addBotMessage(storePolicyAnswer);
      return true;
    }

    const policyOrPricingAnswer = getPolicyOrPricingAnswer(question);
    if (policyOrPricingAnswer) {
      addBotMessage(policyOrPricingAnswer.html);
      return true;
    }

    const localAnswer = getLocalBrandAnswer(question);
    if (localAnswer) {
      addBotMessage(escapeHtml(localAnswer));

      if (
        question.toLowerCase().includes("faction") ||
        question.toLowerCase().includes("story")
      ) {
        addBotMessage(renderWorldMap());
      }

      maybeShowFactionResult(question);
      return true;
    }

    return false;
  }

  async function askBackend(question) {
    const bot = detectBot();

    if (titleEl) {
      titleEl.textContent = BOT_LABELS[bot] || "Storyline Store AI";
    }

    if (!HAS_BACKEND) {
      addBotMessage(
        "I can help with product discovery, route shoppers to GCR, DREDLEN, or Shopper Momma, explain the storyline, and answer store policy and pricing questions."
      );
      return;
    }

    try {
      const res = await fetch(BACKEND_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bot,
          question,
          page: window.location.href,
          ref: document.referrer || "",
          userAgent: navigator.userAgent,
          path: window.location.pathname,
          purchaseVotes: state.purchaseVotes,
          storyState: state.storyState,
          nfcScans: state.nfcScans
        })
      });

      if (!res.ok) {
        addBotMessage("Sorry — the assistant is still connecting.");
        return;
      }

      const data = await res.json();
      const answer = escapeHtml(data.answer || "Here are a few picks:");
      const carousel = renderCarousel(data.products || []);

      if (data.factionVote && FACTIONS[data.factionVote]) {
        voteForFaction(data.factionVote);
      }

      if (data.secretUnlockKey) {
        unlockSecret(data.secretUnlockKey);
      }

      addBotMessage(`${answer}${carousel}`);

      if (data.showWorldMap) {
        addBotMessage(renderWorldMap());
      }
    } catch (err) {
      console.error(err);
      addBotMessage("Sorry — I couldn't reach the recommendation service.");
    }
  }

  async function processUserMessage(text) {
    addUserMessage(text);

    const handledLocally = await tryLocalAnswer(text);
    if (handledLocally) return;

    await askBackend(text);
  }

  window.StorylineAI = {
    voteForFaction,
    unlockSecret,
    recordNfcScan,
    showWorldMap() {
      addBotMessage(renderWorldMap());
    },
    onProductPurchased(meta = {}) {
      const faction = meta.faction || "attention";
      voteForFaction(faction);
    },
    answer(question) {
      return askBackend(question);
    }
  };

  // =========================
  // EVENTS
  // =========================
  if (launcher && panel) {
    launcher.addEventListener("click", () => {
      panel.style.display = panel.style.display === "none" ? "flex" : "none";

      if (panel.style.display === "flex" && messages && messages.children.length === 0) {
        if (titleEl) {
          titleEl.textContent = BOT_LABELS[detectBot()] || "Storyline Store AI";
        }

        addBotMessage(escapeHtml(STORYLINE.greeting));
        renderDraftCountdown();
        maybeHandleNfcUrl();
      }
    });
  }

  if (closeBtn && panel) {
    closeBtn.addEventListener("click", () => {
      panel.style.display = "none";
    });
  }

  if (ctaRow) {
    ctaRow.addEventListener("click", async (event) => {
      const btn = event.target.closest(".ai-quick-btn");
      if (!btn) return;

      const action = btn.getAttribute("data-ai-action");
      const bot = detectBot();

      if (action === "shop") {
        window.open(getShopUrl(bot), "_blank", "noopener,noreferrer");
        return;
      }

      if (action === "collection") {
        window.open(getCollectionUrl(bot), "_blank", "noopener,noreferrer");
        return;
      }

      if (action === "newest") {
        const products = await getNewestDrops(bot);
        addBotMessage(`Here are the newest drops:${renderCarousel(products)}`);
        return;
      }

      if (action === "trending") {
        const products = await getPopularItemsThisWeek(bot);
        addBotMessage(`Here is what's trending right now:${renderCarousel(products)}`);
        return;
      }

      if (action === "faction") {
        addBotMessage(
          `Which faction matches your personality?<br><br>
          <strong>Aggression</strong>: armor, force, high impact<br>
          <strong>Obsession</strong>: collectibles, precision, serialized items<br>
          <strong>Seduction</strong>: rarity, variants, hidden unlocks<br>
          <strong>Attention</strong>: loud graphics, jerseys, public statement pieces`
        );
        addBotMessage(renderWorldMap());
        return;
      }

      if (action === "draft") {
        renderDraftCountdown();
        addBotMessage(`Ask me for Shopper Momma countdown drops or sports-culture styles.`);
        return;
      }

      if (action === "story") {
        addBotMessage(
          `${escapeHtml(STORYLINE.storylineSummary)}<br><br><strong>Purchases are votes.</strong> The faction with the most participation influences the direction of the world.`
        );
        addBotMessage(renderWorldMap());
        return;
      }

      if (action === "refund") {
        addBotMessage(
          `Returns are handled according to our store refund policy.<br><br><a href="/policies/refund-policy" target="_blank" rel="noopener noreferrer">View Refund Policy</a>`
        );
        return;
      }

      if (action === "privacy") {
        addBotMessage(
          `Our privacy policy explains how customer information is collected, used, and protected.<br><br><a href="/policies/privacy-policy" target="_blank" rel="noopener noreferrer">View Privacy Policy</a>`
        );
        return;
      }

      if (action === "terms") {
        addBotMessage(
          `Our terms of service explain the rules and conditions for using the store.<br><br><a href="/policies/terms-of-service" target="_blank" rel="noopener noreferrer">View Terms of Service</a>`
        );
        return;
      }
    });
  }

  if (input) {
    input.addEventListener("keydown", (event) => {
      if (event.key === "Enter" && sendBtn) {
        sendBtn.click();
      }
    });
  }

  if (sendBtn && input) {
    sendBtn.addEventListener("click", async () => {
      const text = input.value.trim();
      if (!text) return;

      input.value = "";
      await processUserMessage(text);
    });
  }
})();

